/**
 * Bảng đường dẫn — MỘT chỗ khai, router · sidebar · liên kết trong trang cùng đọc.
 *
 * Đường dẫn **tiếng Anh** (người dùng 2026-10-13: « URL 100% tiếng Anh »), giống các app khác của monorepo. Nhãn hiển
 * thị vẫn là tiếng Việt — URL là định danh, không phải chữ cho người đọc. Bốn nhóm, khớp bốn nhóm của sidebar:
 *
 * | Nhóm | Tiền tố | Nghĩa |
 * |---|---|---|
 * | Chung | `/attention`, `/overview`, `/integrations` | toàn hệ sinh thái, không chọn app |
 * | Số liệu | `/metrics/*` | thông số app nào cũng có — mặc định « Tất cả app », chọn từng app bằng `?app=` |
 * | Ứng dụng | `/apps/:app/*` | màn RIÊNG của một app |
 * | Hệ thống · Quản trị | `/system/*`, `/admin/*` | hạ tầng và việc nội bộ của chính HQ |
 *
 * Mọi đường dẫn cũ — tiếng Việt (`/can-xu-ly`, `/so-lieu/*`, `/ung-dung/*`…) và các đợt cũ hơn (`/kinh-doanh/*`,
 * `/ky-thuat/*`…) — vẫn chuyển hướng, xem `LEGACY_REDIRECTS`. Link người ta đã gửi cho nhau không được chết.
 */
export const ROUTES = {
  login: "/login",
  changePassword: "/change-password",

  attention: "/attention",
  overview: "/overview",
  /**
   * HQ-2026-005 — tích hợp đối tác: MỘT menu chung, gom theo nhà cung cấp → sản phẩm → app dùng được. Sản phẩm dùng
   * chung giữa nhiều app nên không thuộc riêng app nào (chọn app bằng `?app=` chỉ để lọc).
   */
  integrations: "/integrations",
  integrationAccounts: "/integrations/accounts",
  integrationConnections: "/integrations/connections",
  integrationBindings: "/integrations/bindings",
  integrationRouting: "/integrations/routing",
  integrationEntitlements: "/integrations/entitlements",
  featureReadiness: "/integrations/readiness",
  integrationHealth: "/integrations/health",
  integrationProduct: (code:string) => `/integrations/products/${encodeURIComponent(code)}`,
  labKind: (kind:string) => `/lab?kind=${encodeURIComponent(kind)}`,
  /** HQ-2026-008 — phòng thử Tingee cho MỘT bộ thông số. */
  tingeeLab: (setId: string) => `/integrations/lab/tingee/${encodeURIComponent(setId)}`,
  /** HQ-2026-011 — phòng thử SePay Bank Hub cho MỘT bộ thông số. */
  bankHubLab: (setId: string) => `/integrations/lab/sepay-bankhub/${encodeURIComponent(setId)}`,
  /** HQ-2026-012 — phòng thử HĐĐT (MISA, BKAV, FPT, Viettel, VNPT, M-Invoice, EasyInvoice). */
  einvoiceLab: (setId: string) => `/integrations/lab/einvoice/${encodeURIComponent(setId)}`,
  /** EINV-2026-020 GĐ7 — kênh đại lý HĐĐT của MỘT dịch vụ (vd Mắt Bão) ở một môi trường + quyền bán của Bube. */
  einvoiceAgency: (code: string, env: string) => `/integrations/agency/${encodeURIComponent(code)}/${encodeURIComponent(env)}`,
  /** HQ-2026-013 · ADR-007 — lịch sử phiên bản của MỘT bộ thông số: so sánh, khôi phục, ân hạn. */
  credentialSetHistory: (setId: string) => `/integrations/sets/${encodeURIComponent(setId)}/history`,
  /**
   * PLAT-2026-141 GĐ2 · ADR-041 D14 — PHÒNG THỬ (L1): tài khoản thử của Bube, tách khỏi tích hợp nền tảng (L2). Quyền `hq.lab.*`.
   * Phòng thử của từng bộ vẫn ở `/integrations/lab/*` (link đã gửi cho nhau không chết); `/lab` liệt kê chúng.
   */
  lab: "/lab",
  labFixtures: "/lab/fixtures",
  labRuns: "/lab/runs",
  /** PLAT-2026-111 · ADR-032 — AI theo app: bật / tắt tính năng, model, ngân sách + trạng thái đẩy cấu hình. */
  ai: "/integrations/ai",
  /** PLAT-2026-115 · ADR-033 — hộp thư hỗ trợ CHUNG mọi app; ticket nằm ở app, hub là bàn xử lý. */
  support: "/support",
  supportTicket: (app: string, id: string) => `/support/${encodeURIComponent(app)}/${encodeURIComponent(id)}`,

  audience: "/metrics/users",
  subscriptions: "/metrics/subscriptions",
  revenue: "/metrics/revenue",
  planMapping: "/metrics/plan-mapping",
  feedback: "/metrics/feedback",
  feedbackDetail: (sourceSlug: string, sourceRef: string) =>
    `/metrics/feedback/${encodeURIComponent(sourceSlug)}/${encodeURIComponent(sourceRef)}`,

  app: (slug: string) => `/apps/${encodeURIComponent(slug)}`,
  /** Tích hợp MỘT app dùng được — cùng trang với `integrations`, lọc cố định theo app. */
  appIntegrations: (slug: string) => `/apps/${encodeURIComponent(slug)}/integrations`,
  /** AI của MỘT app — cùng trang với `ai`, lọc cố định theo app. */
  appAi: (slug: string) => `/apps/${encodeURIComponent(slug)}/ai`,
  /** PLAT-2026-114 — quản trị khách của MỘT app: danh sách workspace, chi tiết (+ đổi gói), danh sách user. */
  appWorkspaces: (slug: string) => `/apps/${encodeURIComponent(slug)}/workspaces`,
  appWorkspace: (slug: string, workspaceId: string) =>
    `/apps/${encodeURIComponent(slug)}/workspaces/${encodeURIComponent(workspaceId)}`,
  appUsers: (slug: string) => `/apps/${encodeURIComponent(slug)}/users`,
  /** PLAT-2026-156 — « Lượt hoá đơn » của MỘT app (bube-einvoice): tìm khách, hồ sơ lượt. Sổ ở app, hub chỉ gửi lệnh. */
  appResale: (slug: string) => `/apps/${encodeURIComponent(slug)}/resale`,
  appInvoiceQuota: (slug: string) => `/apps/${encodeURIComponent(slug)}/invoice-quota`,
  /** PLAT-2026-173 — tình trạng máy chủ lấy hóa đơn Việt Nam (chỉ đọc, hỏi bube-einvoice khi mở trang). */
  appAcquisitionOps: (slug: string) => `/apps/${encodeURIComponent(slug)}/acquisition-ops`,
  appInvoiceQuotaWorkspace: (slug: string, workspaceId: string) =>
    `/apps/${encodeURIComponent(slug)}/invoice-quota/${encodeURIComponent(workspaceId)}`,
  /** bube-shop — đầu GỌI của SHOP-2026-291 (HQ-2026-002). */
  shopBankTransfer: "/apps/bube-shop/transfer-confirmation",
  /** bube-shop — thẻ nào hiện ở trang « Tích hợp » của cửa hàng (PLAT-2026-107). */
  shopIntegrationCards: "/apps/bube-shop/integration-cards",
  /** bube-banktransfer — chế độ khai Bube Bank Transfer Hub (HQ-2026-003). */
  bankTransferSharedHub: "/apps/bube-banktransfer/shared-hub",
  /** BANK-2026-039 — báo cáo đối tác (Tingee, SePay): merchant, user, VA, hạn mức, ngân sách, chi phí, đối chiếu. */
  bankTransferPartnerReport: "/apps/bube-banktransfer/partner-report",
  /** PLAT-2026-134 — link App Link phía bt (bube-shop ↔ bt): xem + xử lý tay khi có sự cố. */
  bankTransferAppLinks: "/apps/bube-banktransfer/app-links",

  sources: "/system/sources",
  source: (slug: string) => `/system/sources/${encodeURIComponent(slug)}`,
  reliability: "/system/reliability",
  /** HQ-2026-018 — chi phí hạ tầng (Neon + GCP) theo app, từ bản chụp ngày. */
  cost: "/system/cost",
  /** PLAT-2026-117 — sẵn sàng gửi mail của mọi app. */
  mailStatus: "/system/mail",
  /** PLAT-2026-135 lát I — mail của MỘT app: tuyến, thiếu gì, thư hỏng, vòng đời một thư, lịch sử. */
  mailStatusApp: (app: string) => `/system/mail/${encodeURIComponent(app)}`,
  /** PLAT-2026-135 lát N — tài khoản nhà cung cấp mail (đoạn tĩnh `providers` thắng `:app` khi khớp đường). */
  mailProviders: "/system/mail/providers",
  mailProvider: (setId: string) => `/system/mail/providers/${encodeURIComponent(setId)}`,
  service: (slug: string) => `/system/services/${encodeURIComponent(slug)}`,
  dependencies: "/system/dependencies",
  products: "/system/products",
  product: (slug: string) => `/system/products/${encodeURIComponent(slug)}`,
  ops: "/system/operations",
  run: (runId: string) => `/system/runs/${encodeURIComponent(runId)}`,

  staff: "/admin/staff",
  teams: "/admin/teams",
  ownership: "/admin/ownership",
} as const;

/** Mẫu route có tham số — router khai bằng các mẫu này, link trong trang dùng hàm cùng tên ở `ROUTES`. */
export const ROUTE_PATTERNS = {
  app: "/apps/:slug",
  mailStatusApp: "/system/mail/:app",
  mailProvider: "/system/mail/providers/:setId",
  appIntegrations: "/apps/:slug/integrations",
  appAi: "/apps/:slug/ai",
  appWorkspaces: "/apps/:slug/workspaces",
  appWorkspace: "/apps/:slug/workspaces/:workspaceId",
  appUsers: "/apps/:slug/users",
  appAcquisitionOps: "/apps/:slug/acquisition-ops",
  appResale: "/apps/:slug/resale",
  appInvoiceQuota: "/apps/:slug/invoice-quota",
  appInvoiceQuotaWorkspace: "/apps/:slug/invoice-quota/:workspaceId",
  integrationProduct: "/integrations/products/:code",
  tingeeLab: "/integrations/lab/tingee/:setId",
  bankHubLab: "/integrations/lab/sepay-bankhub/:setId",
  einvoiceLab: "/integrations/lab/einvoice/:setId",
  einvoiceAgency: "/integrations/agency/:code/:env",
  credentialSetHistory: "/integrations/sets/:setId/history",
  source: "/system/sources/:slug",
  service: "/system/services/:slug",
  product: "/system/products/:slug",
  run: "/system/runs/:runId",
  feedbackDetail: "/metrics/feedback/:sourceSlug/:sourceRef",
  supportTicket: "/support/:app/:id",
} as const;

/** Trang « Số liệu » kèm sẵn app đang xem — dùng ở trang tổng quan của từng app. */
export const withApp = (path: string, app: string | null): string =>
  app ? `${path}?app=${encodeURIComponent(app)}` : path;

/** Hai trang ngoài vỏ đăng nhập — chuyển hướng ở cấp gốc của router, không nằm sau `SessionGate`. */
export const PUBLIC_LEGACY_REDIRECTS: { from: string; to: string }[] = [
  { from: "/dang-nhap", to: ROUTES.login },
  { from: "/doi-mat-khau", to: ROUTES.changePassword },
];

/**
 * Đường dẫn cũ → mới. Tham số động (`:slug`, `:runId`…) được thay giữ nguyên tên; query giữ nguyên.
 */
export const LEGACY_REDIRECTS: { from: string; to: string }[] = [
  // ── Tiếng Việt (đến 2026-10-13) ──
  { from: "/can-xu-ly", to: ROUTES.attention },
  { from: "/tong-quan", to: ROUTES.overview },
  { from: "/tich-hop", to: ROUTES.integrations },
  { from: "/so-lieu/tich-hop", to: ROUTES.integrations },
  { from: "/so-lieu/nguoi-dung", to: ROUTES.audience },
  { from: "/so-lieu/goi-dang-ky", to: ROUTES.subscriptions },
  { from: "/so-lieu/doanh-thu", to: ROUTES.revenue },
  { from: "/so-lieu/anh-xa-goi", to: ROUTES.planMapping },
  { from: "/so-lieu/phan-hoi", to: ROUTES.feedback },
  { from: "/so-lieu/phan-hoi/:sourceSlug/:sourceRef", to: ROUTE_PATTERNS.feedbackDetail },
  { from: "/ung-dung/bube-shop/xac-nhan-chuyen-khoan", to: ROUTES.shopBankTransfer },
  { from: "/ung-dung/bube-banktransfer/may-thu-chung", to: ROUTES.bankTransferSharedHub },
  { from: "/ung-dung/:slug", to: ROUTE_PATTERNS.app },
  { from: "/ung-dung/:slug/tich-hop", to: ROUTE_PATTERNS.appIntegrations },
  { from: "/he-thong/nguon", to: ROUTES.sources },
  { from: "/he-thong/nguon/:slug", to: ROUTE_PATTERNS.source },
  { from: "/he-thong/do-tin-cay", to: ROUTES.reliability },
  { from: "/he-thong/dich-vu/:slug", to: ROUTE_PATTERNS.service },
  { from: "/he-thong/phu-thuoc", to: ROUTES.dependencies },
  { from: "/he-thong/san-pham", to: ROUTES.products },
  { from: "/he-thong/san-pham/:slug", to: ROUTE_PATTERNS.product },
  { from: "/he-thong/van-hanh", to: ROUTES.ops },
  { from: "/he-thong/lich-chay/:runId", to: ROUTE_PATTERNS.run },
  { from: "/quan-tri/nhan-vien", to: ROUTES.staff },
  { from: "/quan-tri/doi-ngu", to: ROUTES.teams },
  { from: "/quan-tri/quyen-so-huu", to: ROUTES.ownership },

  // ── Đợt sắp xếp cũ hơn ──
  { from: "/kinh-doanh/nguoi-dung", to: ROUTES.audience },
  { from: "/kinh-doanh/goi-dang-ky", to: ROUTES.subscriptions },
  { from: "/kinh-doanh/doanh-thu", to: ROUTES.revenue },
  { from: "/kinh-doanh/anh-xa-goi", to: ROUTES.planMapping },
  { from: "/kinh-doanh/phan-hoi", to: ROUTES.feedback },
  { from: "/kinh-doanh/phan-hoi/:sourceSlug/:sourceRef", to: ROUTE_PATTERNS.feedbackDetail },
  { from: "/kinh-doanh/xac-nhan-chuyen-khoan", to: ROUTES.shopBankTransfer },
  { from: "/nen-tang/may-thu-chung", to: ROUTES.bankTransferSharedHub },
  { from: "/nen-tang/nguon", to: ROUTES.sources },
  { from: "/nen-tang/nguon/:slug", to: ROUTE_PATTERNS.source },
  { from: "/nen-tang/van-hanh", to: ROUTES.ops },
  { from: "/nen-tang/lich-chay/:runId", to: ROUTE_PATTERNS.run },
  { from: "/ky-thuat/do-tin-cay", to: ROUTES.reliability },
  { from: "/ky-thuat/dich-vu", to: ROUTES.reliability },
  { from: "/ky-thuat/tai-nguyen", to: ROUTES.reliability },
  { from: "/ky-thuat/dich-vu/:slug", to: ROUTE_PATTERNS.service },
  { from: "/ky-thuat/phu-thuoc", to: ROUTES.dependencies },
  { from: "/san-pham", to: ROUTES.products },
  { from: "/san-pham/:slug", to: ROUTE_PATTERNS.product },
];
